import React from "react";
import { Badge, Box, ButtonBase, Divider, Tooltip, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SpeakerNotesOffOutlinedIcon from "@mui/icons-material/SpeakerNotesOffOutlined";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import { getReaderRailPositionSx } from "../../theme/readerLayout";

const RailActionButton = ({
  icon,
  label,
  tooltip,
  ariaLabel,
  onClick,
  active = false,
  badgeCount = 0,
  disabled = false,
}) => (
  <Tooltip title={tooltip || label} placement="left" arrow>
    <span>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel || tooltip || label}
        sx={{
          width: 40,
          minHeight: 44,
          py: 0.6,
          px: 0.25,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          color: active ? "primary.main" : "text.secondary",
          bgcolor: active ? "action.selected" : "transparent",
          transition: "all 150ms ease",
          "&:hover": {
            bgcolor: "action.hover",
            color: "text.primary",
          },
          "&.Mui-disabled": {
            opacity: 0.35,
          },
        }}
      >
        <Badge
          badgeContent={badgeCount > 0 ? badgeCount : null}
          color="primary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.62rem",
              height: 15,
              minWidth: 15,
              px: 0.4,
            },
          }}
        >
          {icon}
        </Badge>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: active ? 700 : 500,
            lineHeight: 1.1,
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      </ButtonBase>
    </span>
  </Tooltip>
);

const ReaderRightRail = ({
  contentWidth = 80,
  isZenMode = false,
  edition,
  hasPrev = false,
  hasNext = false,
  onPrev,
  onNext,
  verseCommentsEnabled = true,
  onToggleVerseComments,
  verseCommentsCount = 0,
  onScrollToTop,
}) => {
  const ui = edition?.ui || {};

  return (
    <Box
      component="aside"
      aria-label={ui.railNext || "章节导航栏"}
      sx={getReaderRailPositionSx("right", contentWidth, { isZenMode })}
    >
      <RailActionButton
        icon={<NavigateBeforeIcon sx={{ fontSize: 20 }} />}
        label={ui.railPrev || "上一章"}
        tooltip={`${ui.prevChapter || "上一章"} (←)`}
        ariaLabel={ui.prevChapter || "上一章"}
        disabled={!hasPrev}
        onClick={onPrev}
      />

      <RailActionButton
        icon={<NavigateNextIcon sx={{ fontSize: 20 }} />}
        label={ui.railNext || "下一章"}
        tooltip={`${ui.nextChapter || "下一章"} (→)`}
        ariaLabel={ui.nextChapter || "下一章"}
        disabled={!hasNext}
        onClick={onNext}
      />

      {onToggleVerseComments && (
        <>
          <Divider flexItem sx={{ my: 0.25 }} />
          <RailActionButton
            icon={
              verseCommentsEnabled ? (
                <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
              ) : (
                <SpeakerNotesOffOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            label={ui.railVerseComments || "节评"}
            tooltip={ui.railVerseComments || "节评开关"}
            ariaLabel={ui.railVerseComments || "节评"}
            active={verseCommentsEnabled}
            badgeCount={verseCommentsEnabled ? verseCommentsCount : 0}
            onClick={onToggleVerseComments}
          />
        </>
      )}

      <Divider flexItem sx={{ my: 0.25 }} />

      <RailActionButton
        icon={<VerticalAlignTopIcon sx={{ fontSize: 18 }} />}
        label={ui.railTop || "顶部"}
        tooltip={ui.scrollToTop || "回到顶部"}
        ariaLabel={ui.scrollToTop || "回到顶部"}
        onClick={onScrollToTop}
      />
    </Box>
  );
};

export default ReaderRightRail;
